import { Button } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import AiInfoBlock from 'components/others/AiInfoBlock';
import { UploadFileProgress } from 'modules/files';
import spinnerTitle from 'components/others/spinnerTitle';
import type { ImportProductItem, ImportVendorLookup } from './model/types';
import useImportIaInfo from './model/useImportIaInfo';
import { getCurrencyI18nParams } from 'utils/helpers/currency/currencyHelpers';

interface ImportIaInfoProps {
    vendors: ImportVendorLookup[];
    itemsCount: number;
    onParsed: (items: ImportProductItem[]) => void;
    pricesInUsd: boolean;
    onPricesInUsdChange: (next: boolean) => void;
    usdRate: number;
    isImporting: boolean;
    onImport: () => void;
}

const ImportIaInfo = ({
    vendors,
    itemsCount,
    onParsed,
    pricesInUsd,
    onPricesInUsdChange,
    usdRate,
    isImporting,
    onImport,
}: ImportIaInfoProps) => {
    const { t } = useTranslation(['Products']);
    const { refInputFile, isBusy, uploadProgress, phase, processingLabel, handleOpenClick, handleChangeFile, handleCancel } =
        useImportIaInfo({ vendors, onParsed });

    return (
        <AiInfoBlock title={t('importIa.infoTitle')}>
            <p>{t('importIa.infoText')}</p>
            <div className="ai-info-block-formats">
                <span className="ai-info-block-formats-label">{t('importIa.formatsLabel')}</span>
                {t('importIa.formatsList')}
            </div>
            {usdRate !== 1 && (
            <div className="form-check form-switch mt-3">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="import-ai-price-usd-switch"
                    checked={pricesInUsd}
                    onChange={(e) => onPricesInUsdChange(Boolean(e.target.checked))}
                />
                <label className="form-check-label" htmlFor="import-ai-price-usd-switch">
                    {t('importCsv.pricesInUsdSwitch', getCurrencyI18nParams())}
                </label>
                {!pricesInUsd && (
                    <div className="small text-muted">
                        {t('importCsv.pricesConvertHint', {
                            ...getCurrencyI18nParams(),
                            rate: Number(usdRate || 0).toFixed(2),
                        })}
                    </div>
                )}
            </div>
            )}
            <div className="d-flex flex-wrap gap-2 mt-3">
                <Button type="button" variant="primary" size="sm" onClick={handleOpenClick} disabled={isBusy}>
                    {spinnerTitle(
                        <>
                            <i className="bi bi-stars me-1"></i>
                            {t('importIa.open')}
                        </>,
                        isBusy,
                        processingLabel,
                    )}
                </Button>
                {phase === 'processing' && (
                    <Button type="button" variant="outline-danger" size="sm" onClick={handleCancel}>
                        <i className="bi bi-stop-circle me-1"></i>
                        {t('importIa.cancel')}
                    </Button>
                )}
                {itemsCount > 0 && (
                    <Button type="button" variant="success" size="sm" disabled={isBusy || isImporting} onClick={onImport}>
                        {spinnerTitle(
                            <>
                                <i className="bi bi-box-arrow-in-down me-1"></i>
                                {t('importCsv.import')}
                            </>,
                            isImporting,
                            t('importCsv.importing'),
                        )}
                    </Button>
                )}
            </div>
            <div className="small text-muted mt-2">
                <Trans i18nKey="importIa.editHint" ns="Products" components={{ strong: <strong /> }} />
            </div>
            {uploadProgress !== false && (
                <div className="pt-2">
                    <UploadFileProgress progress={Number(uploadProgress)} />
                    <div className="small text-muted mt-2">{processingLabel}</div>
                </div>
            )}
            <input ref={refInputFile} type="file" className="d-none" onChange={handleChangeFile} />
        </AiInfoBlock>
    );
};

export default ImportIaInfo;
